import { apiClient } from './apiClient';
import { UpdateClientPayload } from './client';

export type RelationshipType = {
  id: number;
  name: string;
  categoryId: number;
};

export const RELATIONSHIP_CATEGORIES = {
  OWNERSHIPS: {
    id: 1,
    type: 'ownerships',
    context: 'owner',
    label: 'Ownerships',
  },
  INCOME_SOURCES: {
    id: 2,
    type: 'incomeSources',
    context: 'income source',
    label: 'Income sources',
  },
} as const;

export const OWNERSHIP_RELATIONSHIP_TYPE_IDS = new Set([1, 2]);

export type RelationshipActivityType = {
  id: number;
  name: string;
};

export type ClientRelationship = {
  id: number;
  clientId: number;
  relationshipClient?:
    | {
        id: number;
        name: string;
      }
    | NewClient;
  relationshipClientId?: number;
  clientRelationshipType?: {
    id: number;
    name: string;
    categoryId: number;
  };
  clientRelationshipTypeId?: number;
  startingDate: string;
  endingDate: string | null;
  isFinalRelationship: boolean | null;
  areJointSpouses: boolean;
  profit: number;
  loss: number;
  capital: number;
  isActive: boolean | null;
  k1Applicable: boolean | null;
  k1IssuedTo?: {
    id: number;
    name: string;
  };
  k1Provider: number | null;
  quickReminder: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  activityType?: {
    id: number;
    name: string;
  } | null;
};

export type NewClientInfo = {
  name: string;
  federalLegalId: string;
  accountId?: number;
  filingCategoryId?: number;
  isActive?: boolean;
  businessActivityId?: number;
  billingTypeId?: number;
  isClient: boolean;
  dateEstablished: string;
  closeDate?: string;
  isFormedByUs: boolean;
  isFlowThroughEntity: boolean;
  additionalInformation?: string;
  entityType: {
    id: number;
    startingDate: string;
  };
  filingStatus: {
    id: number;
    startingDate: string;
    parent?: NewClient | number;
  };
  domesticState: {
    id: number;
    startingDate: string;
  };
  serviceStartingDate?: string;
  serviceEndingDate?: string;
  initialFilingDate?: string;
  finalFilingDate?: string;
};

export type NewClientAddress = {
  street: string;
  unit?: string;
  city: number;
  state: number;
  zipCode: number;
};

export type NewClientContact = {
  firstName: string;
  lastName: string;
  dob?: string;
  ssn?: string;
  phone?: string;
  cell?: string;
  fax?: string;
  email?: string;
  ccEmails?: string[];
};

export type NewClient = {
  clientInfo: NewClientInfo;
  address?: NewClientAddress;
  contact?: NewClientContact;
  originalGlobalClientId?: number;
};

export type EditedClient = {
  clientId: number;
  clientData: NewClient;
};

export type CreateClientRelationshipPayload = {
  relationshipClient: number | NewClient | EditedClient | null;
  clientRelationshipTypeId: number | null;
  startingDate: string;
  endingDate?: string;
  isFinalRelationship?: boolean;
  areJointSpouses: boolean;
  profit: number;
  loss: number;
  capital: number;
  clientRelationshipActivityTypeId?: number;
  isActive?: boolean;
  k1Applicable?: boolean;
  k1Provider?: number;
  quickReminder?: string;
};

export type UpdateClientRelationshipPayload =
  Partial<CreateClientRelationshipPayload>;

export type RelationshipQueryParams = {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  clientId?: number;
};

export type ResolvedRelationshipPayload = {
  relationshipClient: number;
  clientRelationshipTypeId: number;
  startingDate: string;
  endingDate?: string;
  isFinalRelationship?: boolean;
  areJointSpouses: boolean;
  profit: number;
  loss: number;
  capital: number;
  clientRelationshipActivityTypeId?: number;
  isActive?: boolean;
  k1Applicable: boolean;
  k1Provider?: number;
  quickReminder?: string;
};

export const getRelationshipTypes = async (): Promise<RelationshipType[]> => {
  return apiClient.get('/clients/relationship-types', {});
};

export const getRelationshipActivityTypes = async (): Promise<
  RelationshipActivityType[]
> => {
  return apiClient.get('/clients/relationship-activity-types', {});
};

export type CategorizedRelationships = {
  ownerships: ClientRelationship[];
  incomeSources: ClientRelationship[];
};

export const getClientRelationships = async (
  clientId: number,
  params?: RelationshipQueryParams
): Promise<CategorizedRelationships> => {
  return apiClient.get(`/clients/${clientId}/relationships`, { params });
};

export const createClientRelationship = async (
  clientId: number,
  payload: CreateClientRelationshipPayload
): Promise<ClientRelationship> => {
  return apiClient.post(`/clients/${clientId}/relationships`, payload);
};

export const updateClientRelationship = async (
  clientId: number,
  relationshipId: number,
  payload: UpdateClientRelationshipPayload
): Promise<ClientRelationship> => {
  return apiClient.put(
    `/clients/${clientId}/relationships/${relationshipId}`,
    payload
  );
};

export const deleteClientRelationship = async (
  clientId: number,
  relationshipId: number
): Promise<void> => {
  return apiClient.del(`/clients/${clientId}/relationships/${relationshipId}`);
};

export const validateRelationships = async ({
  clientId,
  relationships,
}: {
  clientId: number;
  relationships: CreateClientRelationshipPayload[];
}): Promise<{
  validationErrors: { fieldName: string; message: string }[];
}> => {
  return apiClient.post(`/clients/${clientId}/validate-relationships`, {
    relationships,
  });
};

export const validateRelationship = async (
  clientId: number,
  payload: UpdateClientPayload
): Promise<{
  validationErrors: { fieldName: string; message: string }[];
}> => {
  return apiClient.post(`/clients/${clientId}/validate-relationships`, payload);
};
